import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { fetchStrategiesForPeriod, fetchAllStrategies } from "../../../../../utils/restAPI";
import {
  selectDetailPeriod,
  selectDetailStrategies,
  setDetailStrategies,
  setDetailLoading,
  setDetailPeriod,
  clearStrategyDetail,
} from "../../../../../Redux/Reducers/strategyStoreSlice";
import {
  transformStrategyData,
  withPeriodCached,
  applyPeriod,
  storePeriod,
} from "../../strategyTransformers";

/**
 * useStrategyComparison
 *
 * Drives the Strategy Comparison page. Designed for N strategies:
 * - strategies[0]  = main strategy (from :strategySlug)
 * - strategies[1+] = comparison strategies (from :compareSlugs, comma-separated)
 *
 * Currently the UI limits comparisons to 1, but the hook and Redux state
 * support any number for future expansion.
 */
const useStrategyComparison = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ownerSlug, strategySlug, compareSlugs } = useParams();

  const period = useSelector(selectDetailPeriod);
  const strategies = useSelector(selectDetailStrategies);

  const [allStrategiesOptions, setAllStrategiesOptions] = useState([]);
  const [isOptionsLoading, setIsOptionsLoading] = useState(true);

  // Support N compare slugs via comma-separated URL param
  const compareSlugList = compareSlugs ? compareSlugs.split(",").filter(Boolean) : [];
  const allSlugs = [strategySlug, ...compareSlugList];

  // Load autocomplete options for the strategy swap dropdown
  useEffect(() => {
    const loadOptions = async () => {
      setIsOptionsLoading(true);
      const response = await fetchAllStrategies();
      if (response.success) setAllStrategiesOptions(response.data);
      setIsOptionsLoading(false);
    };
    loadOptions();
  }, []);

  // Fetch all strategies (main + comparisons) whenever slugs or options change
  useEffect(() => {
    if (!allStrategiesOptions.length) return;

    const fetchAllForPage = async () => {
      dispatch(setDetailLoading(true));
      try {
        // Fetch each strategy individually (same pattern as useStrategyDetail)
        // to guarantee each one is returned regardless of batch API behavior.
        const results = await Promise.all(
          allSlugs.map(async (slug) => {
            const entry = allStrategiesOptions.find((s) => s.slug === slug);
            if (!entry) return null;
            const response = await fetchStrategiesForPeriod(period, 1, 1, "", "owner", entry.strategyId);
            if (response.success && response.data.length > 0) {
              const [strategy] = transformStrategyData(response.data);
              return strategy ?? null;
            }
            return null;
          })
        );

        // Promise.all preserves order, so filter keeps URL order (main first, then compares)
        const ordered = results.filter(Boolean);
        if (ordered.length > 0) {
          dispatch(setDetailStrategies(ordered.map((s) => withPeriodCached(s, period))));
        }
      } catch (error) {
        console.error("Failed to fetch comparison strategies:", error);
      } finally {
        dispatch(setDetailLoading(false));
      }
    };

    fetchAllForPage();
  }, [allStrategiesOptions, strategySlug, compareSlugs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show spinners immediately on mount, cleanup on unmount
  useEffect(() => {
    dispatch(setDetailLoading(true));
    return () => {
      dispatch(clearStrategyDetail());
    };
  }, [dispatch]);

  // Handles period changes for all strategies in the array (main + all comparisons)
  const handlePeriodChange = async (event) => {
    const newPeriod = event.target.value;
    dispatch(setDetailPeriod(newPeriod));

    if (strategies.length === 0) return;

    const uncachedStrategies = strategies.filter((s) => !s.fetchedPeriods?.[newPeriod]);

    if (uncachedStrategies.length === 0) {
      dispatch(setDetailStrategies(strategies.map((s) => applyPeriod(s, newPeriod))));
      return;
    }

    dispatch(setDetailLoading(true));
    try {
      const freshResults = await Promise.all(
        uncachedStrategies.map(async (s) => {
          const response = await fetchStrategiesForPeriod(newPeriod, 1, 1, "", "owner", s.idStrategy);
          if (response.success && response.data.length > 0) {
            const [fresh] = transformStrategyData(response.data);
            return { idStrategy: s.idStrategy, fresh };
          }
          return null;
        })
      );

      const fetchedById = Object.fromEntries(
        freshResults.filter(Boolean).map(({ idStrategy, fresh }) => [idStrategy, fresh])
      );

      dispatch(
        setDetailStrategies(
          strategies.map((s) => {
            if (s.fetchedPeriods?.[newPeriod]) return applyPeriod(s, newPeriod);
            const freshData = fetchedById[s.idStrategy];
            return freshData ? storePeriod(s, newPeriod, freshData) : s;
          })
        )
      );
    } catch (error) {
      console.error("Failed to fetch strategies for period:", error);
    } finally {
      dispatch(setDetailLoading(false));
    }
  };

  /**
   * Replace the compare strategy at compareIndex (0-based within compare list) and navigate.
   * Supports N strategies: replaces just the slug at the given position.
   */
  const handleSwapCompareStrategy = (compareIndex, newSlug) => {
    const updatedCompareSlugs = [...compareSlugList];
    updatedCompareSlugs[compareIndex] = newSlug;
    navigate(`/store/strategy/${ownerSlug}/${strategySlug}/compare/${updatedCompareSlugs.join(",")}`);
  };

  return {
    strategies,
    ownerSlug,
    strategySlug,
    compareSlugList,
    allStrategiesOptions,
    isOptionsLoading,
    period,
    handlePeriodChange,
    handleSwapCompareStrategy,
  };
};

export default useStrategyComparison;
